import React, { useState, useEffect, useMemo } from 'react';
import { SearchResult } from '../../../types/search';
import './SearchPanel.css';

interface SearchPanelProps {
  onSearch: (query: string) => SearchResult[];
  onNavigateToResult: (result: SearchResult) => void;
  currentSectionId?: string;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  onSearch,
  onNavigateToResult,
  currentSectionId
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim()) {
        setIsSearching(true);
        try {
          const searchResults = onSearch(query);
          setResults(searchResults);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setResults([]);
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [query, onSearch]);

  const handleClearSearch = () => {
    setQuery('');
    setResults([]);
  };

  const handleResultClick = (result: SearchResult) => {
    onNavigateToResult(result);
  };

  // Group results by chapter for better organization
  const groupedResults = useMemo(() => {
    const groups = new Map<string, SearchResult[]>();
    
    results.forEach(result => {
      const key = `${result.chapterId}|${result.chapterTitle}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(result);
    });
    
    return Array.from(groups.entries()).map(([key, results]) => {
      const [chapterId, chapterTitle] = key.split('|');
      return { chapterId, chapterTitle, results };
    });
  }, [results]);

  const highlightText = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="search-highlight">{part}</mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="search-panel">
      <div className="search-header">
        <h3>🔍 Search</h3>
      </div>
      
      <div className="search-input-container">
        <input
          type="text"
          placeholder="Search across all chapters..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="search-input"
        />
        {query && (
          <button
            onClick={handleClearSearch}
            className="search-clear-btn"
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      <div className="search-results">
        {isSearching && (
          <div className="search-loading">
            <div className="loading-spinner"></div>
            Searching...
          </div>
        )}

        {!isSearching && query && results.length === 0 && (
          <div className="search-no-results">
            <p>No results found for "{query}"</p>
            <p className="search-tip">Try different keywords or check spelling</p>
          </div>
        )}

        {!isSearching && results.length > 0 && (
          <>
            <div className="search-results-header">
              <span className="search-results-count">
                {results.length} result{results.length !== 1 ? 's' : ''} found
              </span>
            </div>

            <div className="search-results-list">
              {groupedResults.map(({ chapterId, chapterTitle, results: chapterResults }) => (
                <div key={chapterId} className="search-chapter-group">
                  <div className="search-chapter-header">
                    <span className="search-chapter-title">
                      {chapterTitle} ({chapterResults.length})
                    </span>
                  </div>
                  
                  {chapterResults.map((result, index) => (
                    <div
                      key={`${result.sectionId}-${index}`}
                      className={`search-result-item ${
                        result.sectionId === currentSectionId ? 'current' : ''
                      }`}
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="search-result-title">
                        {highlightText(result.sectionTitle, query)}
                      </div>
                      <div className="search-result-context">
                        {highlightText(result.contextPreview, query)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default SearchPanel;
